import type {
  T_ChatV8UpcomingEventItem,
  T_ChatV8UpcomingEventStats,
} from "@lattice/core-lib/types/chat-v8";

/**
 * Shared presentation rules for the upcoming-events card (RAI-1097).
 * Both platforms derive the date badge and stats line from the wire data
 * through these functions so the two cards can never disagree on wording.
 */

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export type T_UpcomingEventDateBadge = {
  /** Day of month, e.g. "5". */
  day: string;
  /** Short month, e.g. "Aug". */
  month: string;
};

/**
 * Derives the date badge from the item's ISO start time. Returns null for an
 * unparsable date — the renderer then omits the badge rather than showing
 * "NaN".
 */
export function getUpcomingEventDateBadge(
  item: T_ChatV8UpcomingEventItem,
): T_UpcomingEventDateBadge | null {
  const parsed = new Date(item.addToCalendar.startsAt);
  if (Number.isNaN(parsed.getTime())) return null;
  const month = MONTH_LABELS[parsed.getUTCMonth()];
  if (month === undefined) return null;
  return { day: String(parsed.getUTCDate()), month };
}

/** One consistent stats line on both platforms. */
export function getUpcomingEventStatsLabel(
  stats: T_ChatV8UpcomingEventStats,
): string {
  if (stats.kind === "estimate") {
    return `Est ${stats.est} · Prev ${stats.prev}`;
  }
  return `${stats.portfolioPercent.toFixed(1)}% of portfolio`;
}
